import React, { useEffect } from "react";

import { SurveyQuestion } from "../components/survey/SurveyQuestion";
import { ISurveyContract } from "../services/zeos-api/contracts/Survey";
import { QuestionTypeEnum } from "../services/zeos-api/enums/Survey";
import {
  isQuestionType,
  isQuestionVisible,
  isQuestionnaireVisible,
  pruneHiddenAnswers,
} from "../services/survey/SurveyVisibilityHelpers";

interface IProps {
  template: ISurveyContract;
  answers: Record<number, any>;
  setAnswers: React.Dispatch<React.SetStateAction<Record<number, any>>>;
  onSubmit: () => void;
}

/**
 * Generic survey form driven by template visibilityRules from the API.
 * Question layout matches Explore survey shell (stacked cards with orange accent).
 */
export const SurveyForm = (props: IProps) => {
  const { template, answers, setAnswers } = props;

  useEffect(() => {
    const pruned = pruneHiddenAnswers(template, answers);
    if (pruned !== answers) {
      setAnswers(pruned);
    }
  }, [answers, setAnswers, template]);

  return (
    <div className="space-y-6">
      {template.questionnaires
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((questionnaire) => {
          if (!isQuestionnaireVisible(questionnaire.visibilityRules, answers)) {
            return null;
          }

          return questionnaire.questions
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((question) => {
              if (!isQuestionVisible(question, answers)) {
                return null;
              }

              const selectHint =
                (isQuestionType(
                  question.questionTypeId,
                  QuestionTypeEnum.MultiChoice
                ) ||
                  isQuestionType(
                    question.questionTypeId,
                    QuestionTypeEnum.RankedChoice
                  )) &&
                question.maxSelect != null
                  ? isQuestionType(
                      question.questionTypeId,
                      QuestionTypeEnum.RankedChoice
                    )
                    ? `Select and rank up to ${question.maxSelect}.`
                    : `Select up to ${question.maxSelect}.`
                  : null;

              return (
                <div
                  key={question.questionId}
                  className="border-l-4 border-zeos-orange-md pl-4 py-2"
                >
                  <div className="text-sm font-semibold text-zeos-black">
                    {question.displayName}
                  </div>
                  {selectHint && (
                    <p className="text-xs text-gray-500 mt-1">{selectHint}</p>
                  )}
                  <div className="mt-3">
                    <SurveyQuestion
                      question={question}
                      isGate={false}
                      answers={answers}
                      setAnswers={setAnswers}
                    />
                  </div>
                </div>
              );
            });
        })}
    </div>
  );
};
